import {
  AbstractMesh,
  Mesh,
  Quaternion,
  Ray,
  Scene,
  SceneLoader,
  Vector3,
} from "@babylonjs/core";
import {
  INTERACTIVE_CONFIG,
  PLAYER_CONFIG,
  PLAYER_MODEL_CONFIG,
} from "../../config/game.config";
import { AnimationSystem } from "../systems/AnimationSystem";
import { InputSystem } from "../systems/InputSystem";
import { PlayerCamera } from "./Camera";
import {
  createBoatDialog,
  createContactDialog,
  createInProgressDialog,
  createSkillStoneDialog,
  createWorkspaceDialog,
} from "./PlayerDialogs";

/**
 * Player – loads the player GLB, wires up all sub-systems, and runs the
 * per-frame update loop (physics, movement, proximity checks).
 */
export default class Player {
  private scene: Scene;

  constructor(scene: Scene) {
    this.scene = scene;
  }

  async createPlayerAndCamera(): Promise<AbstractMesh> {
    // ── Load model ───────────────────────────────────────────────────────────
    const { modelFolder, modelFile } = PLAYER_MODEL_CONFIG;
    const {
      startPosition,
      startRotationY,
      modelScale,
      walkSpeed,
      runSpeed,
      walkBackwardSpeed,
      walkAnimSpeed,
      runAnimSpeed,
      jumpForce,
      gravityForce,
      respawnBelowY,
      wallCheckHeights,
      wallCheckDistance,
    } = PLAYER_CONFIG;

    const startPos = new Vector3(
      startPosition.x,
      startPosition.y,
      startPosition.z
    );

    // ── Camera ───────────────────────────────────────────────────────────────
    const playerCamera = new PlayerCamera(this.scene, startPos);

    // ── Player model ─────────────────────────────────────────────────────────
    const model = await SceneLoader.ImportMeshAsync(
      "",
      modelFolder,
      modelFile,
      this.scene
    );
    model.meshes[0].scaling.setAll(modelScale);
    const player = model.meshes[0];
    const playerMeshSet = new Set(model.meshes);
    const playerMeshesList = model.meshes as Mesh[];

    player.position.set(startPos.x, startPos.y, startPos.z);
    player.rotationQuaternion = Quaternion.RotationAxis(
      Vector3.Up(),
      startRotationY
    );

    // ── Input ────────────────────────────────────────────────────────────────
    const input = new InputSystem(this.scene);

    // ── Animations ───────────────────────────────────────────────────────────
    const anims = new AnimationSystem();
    const getAnim = (name: string) =>
      this.scene.getAnimationGroupByName(name);

    anims.register("idle", getAnim("Idle"));
    anims.register("catWalkForward", getAnim("CatwalkForward"));
    anims.register("runForward", getAnim("RunForward"));
    anims.register("runningJump", getAnim("RunningJump"));
    anims.register("walkBackward", getAnim("WalkBackwards"));
    anims.register("jump", getAnim("Jump"));
    anims.register("danceOne", getAnim("DanceOne"));
    anims.register("turnLeft", getAnim("TurnLeft"));
    anims.register("turnRight", getAnim("TurnRight"));

    // ── UI dialogs ───────────────────────────────────────────────────────────
    const contactDialog = createContactDialog();
    const workspaceDialog = createWorkspaceDialog();
    const boatDialog = createBoatDialog();
    const skillStoneDialog = createSkillStoneDialog();
    const inProgressDialog = createInProgressDialog();

    // ── Physics state ────────────────────────────────────────────────────────
    let isMoving = false;
    let isJumping = false;
    let isDancing = false;
    let verticalVelocity = 0;
    let jumpSpeed = 0;

    // ── Proximity tracking ───────────────────────────────────────────────────
    let contactTableMesh: AbstractMesh | null = null;
    let nearContactTable = false;
    let workspaceMesh: AbstractMesh | null = null;
    let nearWorkspace = false;
    let boatMesh: AbstractMesh | null = null;
    let nearBoat = false;
    let skillStoneMesh: AbstractMesh | null = null;
    let nearSkillStone = false;
    let inProgressMesh: AbstractMesh | null = null;
    let nearInProgress = false;

    // ── Helper: wall-blocked check ───────────────────────────────────────────
    const isWallBlocked = (moveDir: Vector3): boolean => {
      for (const h of wallCheckHeights) {
        const origin = new Vector3(
          player.position.x,
          player.position.y + h,
          player.position.z
        );
        const ray = new Ray(origin, moveDir, wallCheckDistance);
        const hit = this.scene.pickWithRay(
          ray,
          (mesh) => !playerMeshSet.has(mesh)
        );
        if (hit?.hit) return true;
      }
      return false;
    };

    // ── Per-frame loop ───────────────────────────────────────────────────────
    this.scene.onBeforeRenderObservable.add(() => {
      const dt = this.scene.getEngine().getDeltaTime() / 1000;
      const isDirectionalMove =
        input.isPressed("w") ||
        input.isPressed("a") ||
        input.isPressed("d");

      // ── Ground detection (ray cast straight down) ──────────────────────────
      const playerPos = player.position;
      const rayOrigin = new Vector3(playerPos.x, playerPos.y + 2, playerPos.z);
      const downRay = new Ray(rayOrigin, Vector3.Down(), 50);
      const hits = this.scene.multiPickWithRay(
        downRay,
        (mesh) => !playerMeshSet.has(mesh) && !mesh.metadata?.isSea
      );

      let bestGroundY: number | null = null;
      if (hits) {
        for (const h of hits) {
          if (h.hit && h.pickedPoint) {
            const gy = h.pickedPoint.y;
            if (gy <= playerPos.y + 0.3) {
              if (bestGroundY === null || gy > bestGroundY) {
                bestGroundY = gy;
              }
            }
          }
        }
      }

      // ── Vertical physics ───────────────────────────────────────────────────
      if (isJumping) {
        verticalVelocity -= gravityForce * dt;
        player.position.y += verticalVelocity * dt;
      }

      if (bestGroundY !== null) {
        const ground = bestGroundY;
        if (isJumping) {
          if (verticalVelocity <= 0 && player.position.y <= ground) {
            player.position.y = ground;
            verticalVelocity = 0;
            isJumping = false;
            jumpSpeed = 0;
            anims.resetCurrent();
          }
        } else {
          const diff = playerPos.y - ground;
          if (diff > 0.5) {
            player.position.y -= 9.8 * dt;
            if (player.position.y < ground) player.position.y = ground;
          } else if (diff < -0.05) {
            player.position.y +=
              (ground - playerPos.y) * Math.min(1, 10 * dt);
          } else if (Math.abs(diff) > 0.02) {
            player.position.y +=
              (ground - playerPos.y) * Math.min(1, 8 * dt);
          }
        }
      } else if (!isJumping) {
        player.position.y -= 9.8 * dt;
      }

      // ── Respawn ────────────────────────────────────────────────────────────
      if (playerPos.y < respawnBelowY) {
        player.position.set(startPos.x, startPos.y, startPos.z);
        player.rotationQuaternion = Quaternion.RotationAxis(
          Vector3.Up(),
          startRotationY
        );
        verticalVelocity = 0;
        isJumping = false;
        jumpSpeed = 0;
      }

      // ── Camera update (FP/TP, smooth target) ──────────────────────────────
      playerCamera.updatePerFrame(
        player.position,
        dt,
        playerMeshesList,
        player.rotationQuaternion
      );

      // ── GTA-style rotation (TP only) ───────────────────────────────────────
      if (
        !playerCamera.isFirstPerson &&
        isDirectionalMove &&
        player.rotationQuaternion
      ) {
        const cameraDir = player
          .getAbsolutePosition()
          .subtract(playerCamera.camera.position);
        cameraDir.y = 0;
        cameraDir.normalize();

        let targetAngle = Math.atan2(cameraDir.x, cameraDir.z);

        if (input.isPressed("a") && input.isPressed("w")) {
          targetAngle -= Math.PI / 4;
        } else if (input.isPressed("d") && input.isPressed("w")) {
          targetAngle += Math.PI / 4;
        } else if (input.isPressed("a")) {
          targetAngle -= Math.PI / 2;
        } else if (input.isPressed("d")) {
          targetAngle += Math.PI / 2;
        }

        const targetQuat = Quaternion.RotationAxis(Vector3.Up(), targetAngle);
        const currentAngle = player.rotationQuaternion.toEulerAngles().y;
        let angleDiff = targetAngle - currentAngle;
        while (angleDiff > Math.PI) angleDiff -= 2 * Math.PI;
        while (angleDiff < -Math.PI) angleDiff += 2 * Math.PI;

        const turnThreshold = 0.3;
        if (Math.abs(angleDiff) > 0.02) {
          Quaternion.SlerpToRef(
            player.rotationQuaternion,
            targetQuat,
            Math.min(1, 8 * dt),
            player.rotationQuaternion
          );
          if (!isMoving && Math.abs(angleDiff) > turnThreshold) {
            if (angleDiff > 0) {
              anims.play("turnRight", true);
            } else {
              anims.play("turnLeft", true);
            }
          } else {
            anims.stop("turnLeft");
            anims.stop("turnRight");
          }
        } else {
          anims.stop("turnLeft");
          anims.stop("turnRight");
        }
      } else if (!playerCamera.isFirstPerson) {
        anims.stop("turnLeft");
        anims.stop("turnRight");
      }

      // ── Movement & animations ─────────────────────────────────────────────
      const isShift = input.isPressed("shift");
      const isBack = input.isPressed("s");
      const isDanceKey = input.isPressed("b");

      if (
        !playerCamera.isFirstPerson &&
        (isDirectionalMove ||
          isBack ||
          isDanceKey ||
          input.isPressed("space"))
      ) {
        const speed =
          (isShift ? runSpeed : walkSpeed) * dt;
        const animSpeed = isShift ? runAnimSpeed : walkAnimSpeed;
        isMoving = true;

        if (isDancing && (isDirectionalMove || isBack || input.isPressed("space"))) {
          isDancing = false;
        }

        // Running jump
        if (
          input.spaceJustPressed &&
          !isJumping &&
          isDirectionalMove &&
          isShift
        ) {
          input.consumeSpacePress();
          isJumping = true;
          verticalVelocity = jumpForce;
          jumpSpeed = runSpeed * dt;
          anims.play("runningJump", false);
        } else {
          input.consumeSpacePress();
        }

        // Walk / run forward
        if (isDirectionalMove && !isJumping) {
          if (isShift) {
            anims.play("runForward", true, animSpeed);
          } else {
            anims.play("catWalkForward", true, animSpeed);
          }
        }

        // Walk backward
        if (isBack && !isDirectionalMove && !isJumping) {
          anims.play("walkBackward", true, animSpeed);
        }

        // Dance
        if (isDanceKey && !isJumping) isDancing = true;
        if (isDancing && !isJumping) anims.play("danceOne", true);

        // Translate player
        if ((isDirectionalMove || isBack) && !isJumping) {
          const forward = player.forward.normalize();
          const moveDir = new Vector3(forward.x, 0, forward.z).normalize();
          const effectiveSpeed = isBack ? -walkBackwardSpeed * dt : speed;
          if (!isWallBlocked(moveDir)) {
            player.position.x += moveDir.x * effectiveSpeed;
            player.position.z += moveDir.z * effectiveSpeed;
          }
        } else if (isJumping && jumpSpeed !== 0) {
          const forward = player.forward.normalize();
          const moveDir = new Vector3(forward.x, 0, forward.z).normalize();
          if (!isWallBlocked(moveDir)) {
            player.position.x += moveDir.x * jumpSpeed;
            player.position.z += moveDir.z * jumpSpeed;
          }
        }
      } else if (!playerCamera.isFirstPerson && isDancing) {
        anims.play("danceOne", true);
      } else {
        isMoving = false;
        if (!isJumping) {
          anims.play("idle", true);
        } else if (jumpSpeed !== 0) {
          const forward = player.forward.normalize();
          const moveDir = new Vector3(forward.x, 0, forward.z).normalize();
          if (!isWallBlocked(moveDir)) {
            player.position.x += moveDir.x * jumpSpeed;
            player.position.z += moveDir.z * jumpSpeed;
          }
        }
      }

      // ── Proximity: contact table ───────────────────────────────────────────
      if (!contactTableMesh) {
        contactTableMesh =
          this.scene.meshes.find((m) =>
            m.name
              .toLowerCase()
              .includes(INTERACTIVE_CONFIG.contactTable.meshNameContains)
          ) ?? null;
      }
      if (contactTableMesh) {
        const dist = Vector3.Distance(
          player.position,
          contactTableMesh.getAbsolutePosition()
        );
        const isNear =
          dist < INTERACTIVE_CONFIG.contactTable.proximityThreshold;
        if (isNear !== nearContactTable) {
          nearContactTable = isNear;
          isNear ? contactDialog.open() : contactDialog.close();
        }
      }

      // ── Proximity: workspace ───────────────────────────────────────────────
      if (!workspaceMesh) {
        workspaceMesh =
          this.scene.meshes.find((m) => {
            const name = m.name.toLowerCase();
            return INTERACTIVE_CONFIG.workspace.meshNameContains.some((s) =>
              name.includes(s)
            );
          }) ?? null;
      }
      if (workspaceMesh) {
        const dist = Vector3.Distance(
          player.position,
          workspaceMesh.getAbsolutePosition()
        );
        const isNear =
          dist < INTERACTIVE_CONFIG.workspace.proximityThreshold;
        if (isNear !== nearWorkspace) {
          nearWorkspace = isNear;
          isNear ? workspaceDialog.open() : workspaceDialog.close();
        }
      }

      // ── Proximity: boat ───────────────────────────────────────────────────
      if (!boatMesh) {
        boatMesh =
          this.scene.meshes.find((m) => {
            const name = m.name.toLowerCase();
            return INTERACTIVE_CONFIG.boat.meshNameContains.some((s) =>
              name.includes(s)
            );
          }) ?? null;
      }
      if (boatMesh) {
        const dist = Vector3.Distance(
          player.position,
          boatMesh.getAbsolutePosition()
        );
        const isNear = dist < INTERACTIVE_CONFIG.boat.proximityThreshold;
        if (isNear !== nearBoat) {
          nearBoat = isNear;
          isNear ? boatDialog.open() : boatDialog.close();
        }
      }

      // ── Proximity: skill stone ─────────────────────────────────────────────
      if (!skillStoneMesh) {
        skillStoneMesh =
          this.scene.meshes.find((m) => {
            const name = m.name.toLowerCase();
            return INTERACTIVE_CONFIG.skillStone.meshNameContains.some((s) =>
              name.includes(s)
            );
          }) ?? null;
      }
      if (skillStoneMesh) {
        const dist = Vector3.Distance(
          player.position,
          skillStoneMesh.getAbsolutePosition()
        );
        const isNear = dist < INTERACTIVE_CONFIG.skillStone.proximityThreshold;
        if (isNear !== nearSkillStone) {
          nearSkillStone = isNear;
          isNear ? skillStoneDialog.open() : skillStoneDialog.close();
        }
      }

      // ── Proximity: in-progress sign ───────────────────────────────────────
      if (!inProgressMesh) {
        inProgressMesh =
          this.scene.meshes.find((m) => {
            const name = m.name.toLowerCase();
            return INTERACTIVE_CONFIG.inProgressSign.meshNameContains.some(
              (s) => name.includes(s)
            );
          }) ?? null;
      }
      if (inProgressMesh) {
        const dist = Vector3.Distance(
          player.position,
          inProgressMesh.getAbsolutePosition()
        );
        const isNear =
          dist < INTERACTIVE_CONFIG.inProgressSign.proximityThreshold;
        if (isNear !== nearInProgress) {
          nearInProgress = isNear;
          isNear ? inProgressDialog.open() : inProgressDialog.close();
        }
      }
    });

    return player;
  }

}
